import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "@/lib/cn";

// The SkillPanel Design System's buttons (DESIGN.md → Buttons): hover and press take the next step
// of the scale, and every variant shows the Focus Halo ring hugging it.
const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none unavailable:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:flex-none",
  // aria-disabled keeps a button focusable, e.g. so its title can say why it is off; `unavailable:`
  // gives it the disabled look and holds it on hover
  {
    variants: {
      variant: {
        // Solid: the one primary action of a view or dialog; Deep Violet while pressed or focused
        default:
          "bg-primary text-primary-foreground hover:bg-primary-hover focus-visible:bg-primary-fg active:bg-primary-fg unavailable:bg-border unavailable:text-muted-foreground/50",

        // The confirm step of an action that cuts something off or cannot be undone
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-hover active:bg-destructive-fg unavailable:bg-border unavailable:text-muted-foreground/50",

        // The standard secondary action
        outline:
          "border border-primary bg-background text-primary-fg hover:border-primary-hover hover:bg-primary-subtle active:border-primary-hover active:bg-primary-muted unavailable:border-subtle-foreground unavailable:bg-background unavailable:text-subtle-foreground",

        // Tertiary actions and toolbars; the border appears on hover and press
        ghost:
          "border border-transparent text-primary-fg hover:border-primary-hover hover:bg-primary-subtle active:border-primary-hover active:bg-primary-muted unavailable:border-transparent unavailable:bg-transparent unavailable:text-subtle-foreground",

        // A row's "Remove" or "Revoke" before its inline confirmation
        "ghost-destructive":
          "border border-transparent text-destructive-fg hover:border-destructive-hover hover:bg-destructive-subtle active:border-destructive-hover active:bg-destructive-muted unavailable:border-transparent unavailable:bg-transparent unavailable:text-subtle-foreground",

        // Plain: text only, for links in running text
        link: "text-primary-fg underline-offset-4 hover:underline unavailable:text-subtle-foreground unavailable:no-underline",

        // Solid on the front door, at size xl
        brand:
          "bg-primary text-primary-foreground hover:bg-primary-hover hover:shadow-elegant focus-visible:bg-primary-fg active:bg-primary-fg unavailable:bg-border unavailable:text-muted-foreground/50 unavailable:shadow-none",
      },
      size: {
        // Inside a table row or a dense toolbar
        xs: "h-6 px-2 text-xs",
        sm: "h-8 px-3",
        // The primary action beside a view title, and inside filter bars
        md: "h-9 px-4",
        // Full-width mobile forms, beside 40px fields
        lg: "h-10 px-4",
        // The front door: sign-in, password and consent screens. It speaks skillpanel.com, not the
        // admin app: a pill with a bold 16px label, as tall as the 48px fields beside it
        xl: "h-12 rounded-full px-8 text-base font-bold",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "sm",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

const Button = ({ className, variant, size, asChild = false, ref, ...props }: ButtonProps) => {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
};
Button.displayName = "Button";

export { Button, buttonVariants };
